import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { getProductsAutocomplete, getBins, updateProductLocation, getProductsByBin } from '../../services/productapi';
import { Html5QrcodeScanner } from 'html5-qrcode';

const LocationPage = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedBin, setSelectedBin] = useState(null);
  const [products, setProducts] = useState([]);
  const [bins, setBins] = useState([]);
  const [binProducts, setBinProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [binsLoading, setBinsLoading] = useState(false);
  const [binProductsLoading, setBinProductsLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [qrScanActive, setQrScanActive] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [binSearchTerm, setBinSearchTerm] = useState('');
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [scannerDialogOpen, setScannerDialogOpen] = useState(false);
  const scannerRef = useRef(null);
  const scannerContainerId = "qr-reader";

  // Fetch bins on component mount
  useEffect(() => {
    fetchBins();
  }, []);

  // Fetch products when search term changes
  useEffect(() => {
    if (productSearchTerm) {
      const timeoutId = setTimeout(() => {
        fetchProducts(productSearchTerm);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [productSearchTerm]);

  // Fetch products in the bin when a bin is selected
  useEffect(() => {
    if (selectedBin) {
      fetchProductsInBin(selectedBin.name);
    } else {
      // Clear bin products when no bin is selected
      setBinProducts([]);
    }
  }, [selectedBin]);

  // Initialize QR scanner when dialog opens
  useEffect(() => {
    if (scannerDialogOpen) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        initQrScanner();
      }, 100);
      return () => clearTimeout(timeoutId);
    } else {
      // Clean up scanner when dialog closes
      if (scannerRef.current) {
        try {
          scannerRef.current.clear();
        } catch (error) {
          console.error("Failed to clear scanner:", error);
        }
        scannerRef.current = null;
      }
    }
  }, [scannerDialogOpen]);

  // Clean up QR scanner on component unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try {
          scannerRef.current.clear();
        } catch (error) {
          console.error("Failed to clear scanner:", error);
        }
      }
    };
  }, []);

  // Initialize the QR code scanner
  const initQrScanner = () => {
    const html5QrcodeScanner = new Html5QrcodeScanner(
      scannerContainerId,
      { 
        fps: 10,
        qrbox: { width: 250, height: 250 },
        rememberLastUsedCamera: true,
      },
      /* verbose= */ false
    );
    
    html5QrcodeScanner.render(onScanSuccess, onScanFailure);
    scannerRef.current = html5QrcodeScanner;
  };

  // Handle successful QR scan
  const onScanSuccess = (decodedText) => {
    console.log(`QR Code detected: ${decodedText}`);
    
    // Find matching bin by name (assuming QR code contains the bin name)
    const matchingBin = bins.find(bin => bin.name === decodedText);
    
    if (matchingBin) {
      setSelectedBin(matchingBin);
      setScannerDialogOpen(false); // Close the scanner dialog
      showNotification('Bin location scanned successfully', 'success');
      
      // Remove focus from any active element
      if (document.activeElement) {
        document.activeElement.blur();
      }
    } else {
      showNotification('No matching bin found for scanned code', 'error');
    }
  };

  // Handle QR scan errors
  const onScanFailure = (error) => {
    // Don't show errors for not finding QR code in each frame
    if (error?.toString().includes("QR code parse error")) {
      return;
    }
    console.error('QR scan error:', error);
  };

  const fetchProducts = async (query) => {
    try {
      setProductsLoading(true);
      const response = await getProductsAutocomplete(query);
      setProducts(response.data);
      setProductsLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProductsLoading(false);
      showNotification('Failed to fetch products', 'error');
    }
  };

  const fetchBins = async () => {
    try {
      setBinsLoading(true);
      const response = await getBins();
      setBins(response.data);
      setBinsLoading(false);
    } catch (error) {
      console.error('Error fetching bins:', error);
      setBinsLoading(false);
      showNotification('Failed to fetch bin locations', 'error');
    }
  };

  const fetchProductsInBin = async (binName) => {
    try {
      setBinProductsLoading(true);
      const response = await getProductsByBin(binName);
      setBinProducts(response.data.products || []);
      setBinProductsLoading(false);
    } catch (error) {
      console.error('Error fetching products in bin:', error);
      setBinProductsLoading(false);
      showNotification('Failed to fetch products in bin', 'error');
    }
  };

  const handleProductSearch = (event, value) => {
    setProductSearchTerm(value);
  };

  const handleBinSearch = (event, value) => {
    setBinSearchTerm(value);
    
    // If QR scanning is active and there's a matching bin
    if (qrScanActive && value) {
      const matchingBin = bins.find(bin => bin.name.toLowerCase() === value.toLowerCase());
      if (matchingBin) {
        setSelectedBin(matchingBin);
        setQrScanActive(false);
        showNotification('Bin location scanned successfully', 'success');
      }
    }
  };

  const startQrScanning = () => {
    setScannerDialogOpen(true);
  };

  const updateLocation = async () => {
    if (!selectedProduct || !selectedBin) {
      showNotification('Please select both a product and a bin location', 'error');
      return;
    }

    try {
      setUpdateLoading(true);
      await updateProductLocation(selectedProduct.id, selectedBin.id);
      
      // Refresh products in the bin to show the updated list
      if (selectedBin) {
        await fetchProductsInBin(selectedBin.name);
      }
      
      setUpdateLoading(false);
      showNotification('Product location updated successfully', 'success');
      
      // Clear product selection but keep the bin selected
      setSelectedProduct(null);
      setProductSearchTerm('');
    } catch (error) {
      console.error('Error updating product location:', error);
      setUpdateLoading(false);
      showNotification('Failed to update product location', 'error');
    }
  };

  const resetForm = () => {
    setSelectedProduct(null);
    setSelectedBin(null);
    setProductSearchTerm('');
    setBinSearchTerm('');
    setBinProducts([]);
    setQrScanActive(false);
  };

  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  return (
    <Box sx={{ padding: { xs: 0, md: 3 } }}>
      <Breadcrumbs items={[
        { label: 'Inventory Management', href: '/inventory' },
        { label: 'Location Management' }
      ]} />

    
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Update Product Location
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, flexWrap: {xs: 'wrap', sm: 'nowrap'} }}>
        <Autocomplete
          sx={{ 
            minWidth: '200px',
            flex: 1 
          }}
          options={products}
          getOptionLabel={(option) => option.name}
          value={selectedProduct}
          onChange={(event, newValue) => {
            setSelectedProduct(newValue);
          }}
          onInputChange={handleProductSearch}
          loading={productsLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select Product"
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText="No products found"
          filterOptions={(x) => x} // Disable client-side filtering
        />

        <Autocomplete
          id="bin-autocomplete"
          sx={{ 
            minWidth: '200px',
            flex: 1
          }}
          options={bins}
          getOptionLabel={(option) => option.name}
          value={selectedBin}
          onChange={(event, newValue) => {
            setSelectedBin(newValue);
            setQrScanActive(false);
            
            // Remove focus from the input after selection
            if (document.activeElement) {
              document.activeElement.blur();
            }
          }}
          onInputChange={handleBinSearch}
          loading={binsLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={qrScanActive ? "Scanning for bin..." : "Select Bin Location"}
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {binsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText="No bin locations found"
        />

        <Tooltip title="Scan QR Code for Bin Location">
          <IconButton 
            onClick={startQrScanning} 
            color={qrScanActive ? "primary" : "default"}
            sx={{ 
              height: 40,
              width: 40,
              bgcolor: qrScanActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              border: '1px solid',
              borderColor: qrScanActive ? 'primary.main' : 'divider',
              borderRadius: 1
            }}
          >
            <QrCodeScannerIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button 
          variant="outlined" 
          onClick={resetForm}
          startIcon={<RefreshIcon />}
          sx={{ textTransform: 'none', borderRadius: 1 }}
        >
          Reset
        </Button>
        <Button 
          variant="contained"
          onClick={updateLocation}
          disabled={!selectedProduct || !selectedBin || updateLoading}
          startIcon={updateLoading ? <CircularProgress size={20} /> : null}
          sx={{ 
            bgcolor: '#e0e0e0', 
            color: 'rgba(0, 0, 0, 0.87)',
            '&:hover': { bgcolor: '#d5d5d5' },
            textTransform: 'none',
            borderRadius: 1
          }}
        >
          {updateLoading ? 'Updating...' : 'Update Location'}
        </Button>
      </Box>

      

      {/* Display products in the selected bin */}
      {selectedBin && (
        <Paper sx={{ p: { xs: 1, md: 3 }, mt: { xs: 1, md: 3 }, borderRadius: 2 }} elevation={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Products in Bin: {selectedBin.name}
            </Typography>
            {binProductsLoading && <CircularProgress size={20} />}
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {binProducts.length > 0 ? (
            <TableContainer>
              <Table sx={{ minWidth: 0 }} aria-label="products in bin table">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'rgba(0, 0, 0, 0.04)' } }}>
                    <TableCell>Product Name</TableCell>

                    <TableCell align="right">Total Stock</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {binProducts.map((product) => (
                    <TableRow
                      key={product.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {product.name}
                      </TableCell>
                      <TableCell align="right">
                        {product.total_stock || 0}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {binProductsLoading ? 'Loading products...' : 'No products found in this bin.'}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* QR Scanner Dialog */}
      <Dialog
        open={scannerDialogOpen}
        onClose={() => setScannerDialogOpen(false)}
        aria-labelledby="qr-scanner-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="qr-scanner-dialog-title">
          Scan QR Code for Bin Location
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            width: '100%', 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div id={scannerContainerId} style={{ width: '100%' }}></div>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Position the QR code within the scanner area
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScannerDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationPage;
